<template>
    <div
        class="o-map-exploration-views"
        :class="{'view-ready': isViewReady && !isViewInTransition && isActive}"
    >
        <div>
            <BaseView
                v-if="mapViewer.activeView === 'OutdoorView'"
                name="OutdoorView"
                key="OutdoorView"

                @coverClick="getOutdoorViewConfig('events.cover_click', false) && goToFloorSelectionView($event)"
                @floorClick="getOutdoorViewConfig('events.floor_click', false) && goToFloorSelectionView($event)"
                @unitClick="getOutdoorViewConfig('events.unit_click', false) && onUnitClick($event)"
                @unitMouseOver="getOutdoorViewConfig('events.mouse_over', false) && onUnitMouseOver($event)"

                :highlight-units-on-mouse-over="getOutdoorViewConfig('highlight_units_on_mouse_over', false)"
                :highlight-covers-on-mouse-over="getOutdoorViewConfig('highlight_covers_on_mouse_over', false)"

                :transition-duration="transitionDuration"
                :allow-rotation="getMapConfigProp('explore_camera.allow_orientation', false) || canOrient"

                :camera-orientation="!isCompassOrientationMode && getOutdoorViewConfig('orientation',-9)"
                :camera-tilt="getOutdoorViewConfig('tilt',35)"

                :zoom-on-units__="zoomOnUnitNames"
                :zoom-on-floors="indoorFloorsUnitNames"
                :zoom="getOutdoorViewConfig('zoom.value',2000)"
                :camera-zoom-margin="getOutdoorViewConfig('zoom.margin',0)"
                :limit-max-zoom-to="getOutdoorViewConfig('zoom.max_zoom',12000)"

                :max-zoom-margin="0.0"

                :limit-min-zoom-to="getOutdoorViewConfig('zoom.min_zoom', 100)"

                :show-covers="true"
                :show-outdoor="showOutdoor"
                :show-floors="outdoorFloorsUnitNames"
                :show-floors-on-start="allFloorsUnitNames"

                :show-icons="outdoorViewIcons"

                :highlight-units="unitsHighlightColors"
                :highlight-floor-walls="floorsWallsHighlightColors"

                :center-on-units__="selectedObjectsUnitNames.length ? selectedObjectsUnitNames : targetZoneServicesUnitNames"
                @transitionComplete="transitionDuration = 1"

                @transitionStart="onTransitionStart"
                @transitionEnd="onTransitionEnd"
            >
                <MapLabel
                    v-for="building in indoorBuildings"
                    :key="building.id"
                    :selectedUnit="building.floor.unit_name"
                    target="floor"
                    :offsetX="0"
                    :offsetY="27"
                    position-x="left"
                    position-y="bottom"
                >
                    <!--
                        <div class="building-label">{{ t(building, 'name') }}</div>
                    -->
                    <ADialog
                        icon="marker"
                        :fill="getOutdoorViewConfig('labels.buildings.marker.color','')"
                        :cursorPointer="getOutdoorViewConfig('events.cover_click', false)"
                        @click="getOutdoorViewConfig('events.cover_click', false) && goToFloorSelectionView(building.meta.building_index)"
                    >
                        {{ t(building, 'name') }}
                    </ADialog>
                </MapLabel>
            </BaseView>
            <BaseView
                v-if="mapViewer.activeView === 'FloorSelection'"
                name="FloorSelection"
                key="FloorSelection"

                @floorClick="onFloorClick"
                @floorMouseOver="onFloorMouseOver"
                :highlight-floors-on-mouse-over="true"

                :center-on-floors="activeFloorUnitNames"
                :zoom-on-floors="activeBuildingFloorsIDs"
                :camera-zoom-margin="0.1"

                :show-covers="buildingGroupViewCoversBuildingIds"
                :show-outdoor="buildingGroupViewShowOutdoor"
                :show-floors="buildingGroupViewVisibleFloorsUnitNames"
                :show-floors-on-start="allFloorsUnitNames"

                :fade-out-floors-except="activeBuildingFloorsIDs"

                :show-icons="activeBgFloorsIcons"

                :transparent-covers="buildingIds"
                :transparent-floors="activeBuildingGroupTransparentFloorsUnitNames"

                :camera-orientation="!isCompassOrientationMode && activeBuildingOrientation"
                :camera-tilt="32"
                :separate-distance="35"
                :separate-floors="activeBuildingGroupSeparateFloors"

                :limit-max-zoom-to="getMapConfigProp('map_views.default.max_zoom',12000)"
                :max-zoom-margin="0.2"

                :limit-min-zoom-to="getBgViewConfig('zoom.min_zoom', 100)"
                :transition-duration="transitionDuration"

                :highlight-floor-walls="floorsWallsHighlightColors"

                @transitionStart="onTransitionStart"
                @transitionEnd="onTransitionEnd"
            >
                <div v-if="getBgViewConfig('show_hover_floor_label', false)">
                    <MapLabel
                        v-if="hoveredFloor"
                        :selectedUnit="hoveredFloorUnitName"
                        target="floor"
                        @click.native="goToLevelView(hoveredFloor.meta.building_index, hoveredFloor.meta.level)"
                    >
                        <ADialog>
                            {{ t(hoveredFloor, 'name')}}
                        </ADialog>

                    </MapLabel>
                </div>

                <div v-if="getBgViewConfig('show_floor_labels', false)">
                    <MapLabel
                        v-for="floor in activeBuildingGroupFloors"
                        :key="floor.id"
                        :selectedUnit="floor.unit.name"
                        target="floor"
                        @click.native="goToLevelView(hoveredFloor.meta.building_index, hoveredFloor.meta.level)"
                    >
                        <ADialog>
                            {{ t(floor, 'name')}}
                        </ADialog>

                    </MapLabel>
                </div>

                <AButton
                    class="ui ui-map-buttons"
                    :text="$t('o-map-exploration-views_to-outdoor')"
                    @onClick="goToOutdoorView()"
                >
                    <template #left>
                        <span class="map-exploration-views__icon-wrapper">
                            <svg-icon
                                original
                                name="icons/arrow-left"
                            />
                        </span>
                    </template>
                </AButton>
            </BaseView>
            <BaseView
                v-if="mapViewer.activeView === 'LevelView'"
                name="LevelView"
                key="LevelView"

                @floorClick="getFloorViewConfig('events.floor_click', false) && onFloorClick($event)"
                @unitClick="getFloorViewConfig('events.unit_click', false) && onUnitClick($event)"
                @unitMouseOver="getFloorViewConfig('events.mouse_over', false) && onUnitMouseOver($event)"

                :render-trigger="renderTrigger"

                :highlight-units-on-mouse-over="getFloorViewConfig('highlight_units_on_mouse_over', false)"

                :center-on-units="selectedObjectsUnitNames"
                :center-on-units-2="objectsInFocusUnitNames"
                :center-on-floors="activeFloorUnitNames"

                :fade-out-floors-except="activeLevelFloorIDs"
                :show-covers="floorViewCoversBuildingIds"

                :camera-orientation="!isCompassOrientationMode && activeBuildingOrientation"
                :camera-tilt="getFloorViewConfig('tilt',32)"
                :camera-zoom-margin="getFloorViewConfig('zoom.margin',0.2)"

                :zoom-on-units--off="selectedObjectsUnitNames"
                :zoom-on-floors--off="activeFloorUnitNames"
                :zoom="getFloorViewConfig('zoom.value',1200)"
                :limit-max-zoom-to="getFloorViewConfig('zoom.max_zoom',12000)"

                :max-zoom-margin="0.2"

                :limit-min-zoom-to="getFloorViewConfig('zoom.min_zoom',100)"

                :show-outdoor="floorViewShowOutdoor"
                :show-floors="floorViewVisibleFloorsUnitNames"
                :show-floors-on-start_="allFloorsUnitNames"
                :show-icons="levelViewIcons"

                :highlight-units="unitsHighlightColors"
                :highlight-floor-walls="floorsWallsHighlightColors"

                :transparent-covers_="buildingIds"
                :transparent-floors_="activeBuildingGroupTransparentFloorsUnitNames"

                :separate-floors="activeBuildingGroupSeparateFloors"

                :transition-duration="transitionDuration"

                @transitionStart="onTransitionStart"
                @transitionEnd="onTransitionEnd"
            >
                <AButton
                    v-if="getFloorViewConfig('ui.go_to_floor_selection_button', true)"
                    class="ui ui-map-buttons"
                    :text="$t('o-map-exploration-views_to-level')"
                    @onClick="goToFloorSelectionView(activeBuildingId, activeLevel)"
                >
                    <template #left>
                        <span class="map-exploration-views__icon-wrapper">
                            <svg-icon
                                original
                                name="icons/arrow-left"
                            />
                        </span>
                    </template>
                </AButton>
            </BaseView>
        </div>

        <div class="hover-labels ui-map-labels" v-if="isActive">
            <MapLabel
                v-if="hoveredUnitObjects.length && getFloorViewConfig('labels.hovered', false)"
                :selectedUnit="hoveredUnitName"

                target="unit"
                :offsetX="0"
                :offsetY="27"
                position-x="left"
                position-y="bottom"
                @click.native="selectUnitObjects(hoveredUnitName)"
            >
                <ADialog
                    icon="marker"
                    :is-flexible="true"
                >
                    <div
                        v-for="obj in hoveredUnitObjects"
                        :key="obj.id"
                        class="guide-bubble"
                    >
                        <img
                            @click.stop="requestGuideTo(obj)"
                            class="guide-button"
                            src="../../../assets/images/icons/directions.png"
                            :alt="`Show guide to ${t(obj,'name')}`"
                            :title="`Show guide to ${t(obj,'name')}`"
                        />
                        <span>{{ t(obj,'name') }}</span>

                        <!--
                        <svg-icon
                            original
                            name="icons/marker"
                            class="a-guide-overview-button__marker"
                            @click.prevent="requestGuideFrom(obj)"
                        />
                        <img
                            v-if="t(obj, 'icon')"
                            :src="t(obj, 'icon')"
                            :alt="t(obj, 'name')"
                            class="o-map-exploration-views__label-img"
                        />
                        <h3>
                            {{ t(obj, 'name')}}
                        </h3>
                        <svg-icon
                            original
                            name="icons/finish-red"
                            class="a-guide-overview-button__finish"
                            @click.prevent="requestGuideTo(obj)"
                        />-->
                    </div>
                </ADialog>
            </MapLabel>

            <div v-if="selectedObjects.length && getFloorViewConfig('labels.selected.simple', false)">
                <MapLabel
                    v-for="unitName in selectedObjectsUnitNames"
                    :key="unitName"
                    :selectedUnit="unitName"
                    target="unit"
                    :offsetX="0"
                    :offsetY="27"
                    position-x="middle"
                    position-y="top"
                >
                    <ADialog
                        :is-flexible="true"
                        pointerPosition="middle-bottom"
                        font-size="14px"
                    >
                        <div
                            v-for="obj in selectedObjectsByUnitName(unitName)"
                            :key="obj.id"
                            class="guide-bubble"
                        >
                            <span>{{ t(obj,'name') }}</span>
                        </div>
                    </ADialog>
                </MapLabel>
            </div>
            <div v-else-if="selectedObjects.length && getFloorViewConfig('labels.selected', true)">
                <MapLabel
                    v-for="unitName in selectedObjectsUnitNames"
                    :key="unitName"
                    :selectedUnit="unitName"
                    target="unit"
                    :offsetX="0"
                    :offsetY="27"
                    position-x="left"
                    position-y="bottom"
                >
                    <ADialog
                        icon="marker"
                        :is-flexible="true"
                    >
                        <div
                            v-for="obj in selectedObjectsByUnitName(unitName)"
                            :key="obj.id"
                            class="guide-bubble"
                        >

                            <img
                                @click.stop="requestGuideTo(obj)"
                                class="guide-button"
                                src="../../../assets/images/icons/directions.png"
                                :alt="`Show guide to ${t(obj,'name')}`"
                                :title="`Show guide to ${t(obj,'name')}`"
                            />
                            <span>{{ t(obj,'name') }}</span>
                        </div>
                    </ADialog>
                </MapLabel>
            </div>
        </div>

    </div>
</template>

<script>
import {mapGetters, mapState} from 'vuex';

// import AButton from "../../atoms/a-button/a-button";
import MapLabel from "../../../shared/components/map-viewer/labels/map-label"
import BaseView from "../../../shared/components/map-viewer/views/base/base-view";
import OutdoorView from "../../../shared/components/map-viewer/views/outdoor/outdoor-view"

import {
    MAP_SHOW_FLOOR,
    MAP_SHOW_OBJECT,
    SET_GYROSCOPE_STATE,
} from "@/shared/components/map-viewer/constants/map-commands";
import {
    MAP_ICONS_LOADED,
    MAP_OBJECT_SELECTED
} from "@/shared/components/map-viewer/constants/map-events";
import AButton from "@/components/atoms/a-button/a-button";
import ADialog from "@/components/atoms/a-dialog/a-dialog";
import {arrayValues2ObjectKeys, objects2UnitColors} from "@/utils/helpers/common";

export default {
    name: "OMapExplorationViews",
    components: {
        ADialog,
        AButton,
        BaseView,
        MapLabel,
    },

    // -------------------------------- Injected ----------------------------------------
    inject: ['mapViewer'],

    // -------------------------------- Data --------------------------------------------
    data(){
        return {
            prevActiveView: 'OutdoorView',
            activeBuildingId: null,
            activeLevel: null,
            activeFloorUnitName: null,
            activeUnitName: null,

            objectsInFocus: [],
            floorsInFocus: [],
            selectedObjects: [],

            hoveredUnitName: null,
            hoveredFloorUnitName: null,

            zoomBasedTilt: 70,
            canOrient: false,
            rotateInterval: null,
            separateDistance: 35,

            prevViewFloorsUnitNames: [],
            hoverDelayTimeout: null,
            isViewInTransition: false,
            isViewReady: false,
            areIconsLoaded: false,
            renderTrigger: 0,
        }
    },

    // -------------------------------- Computed ----------------------------------------
    computed: {
        ...mapState({
            isCompassOrientationMode: state => state.isCompassOrientationMode,
        }),
        ...mapGetters([
            't',
            'getMap',
            'getMapConfig',
            'getMapConfigProp',
        ]),
        isActive(){
            return [
                'OutdoorView',
                'FloorSelection',
                'LevelView',
            ].includes(this.activeView)
        },
        emptyArray(){
            return []
        },
        activeView(){
            return this.mapViewer.activeView;
        },
        md(){
            return this.mapViewer.mapData
        },
        activeFloor(){
            return this.floors.find( f => f.meta.building_index === this.activeBuildingId && f.meta.level === this.activeLevel)
        },
        activeFloorUnitNames(){
            return this.activeFloor ? [this.activeFloor.unit.name] : this.emptyArray
        },
        activeFloorPortals(){
            // TODO: move allowed portal types to a guide settings
            return this.portals.filter( p => p.floor.unit_name === this.activeFloorUnitName && p.meta.portal_type === 'Elevators')
        },

        transitionDuration(){
            if(this.$route.query['map.transition']){
                return Number(this.$route.query['map.transition'])
            }
            return this.mapViewer.$data.isFirstView ? 0 : this.getOutdoorViewConfig('transition_duration', 1.4);
        },

        visibleFloorsUnitNames(){
            return [
                ...this.activeBuildingGroupFloorsUnitNames,
                ...this.outdoorFloorsUnitNames,
            ]
        },
        buildingGroupViewVisibleFloorsUnitNames(){
            const floors = [];
            floors.push(...this.activeBuildingGroupFloorsUnitNames);

            this.getBgViewConfig('show_outdoor', false) &&
                floors.push(...this.outdoorFloorsUnitNames);

            return floors;
        },
        buildingGroupViewCoversBuildingIds(){
            return this.getBgViewConfig('show_outdoor', false) ? this.nonActiveBuildingGroupBuildingIDs : [];
        },
        buildingGroupViewShowOutdoor(){
            return this.getBgViewConfig('show_outdoor', false)
        },

        floorViewVisibleFloorsUnitNames(){
            const floors = [];

            floors.push(...this.activeBuildingGroupLevelFloorsUnitNames);

            if(  this.getFloorViewConfig('floors.above', false) ){
                floors.push(...this.activeBuildingGroupFloorsUnitNames);
            }
            if(  this.getFloorViewConfig('floors.below', false) ){

                const activeLevel = this.activeLevel;
                const belowFloors = this.activeBuildingGroupFloors.filter( f => f.meta.level < activeLevel);
                floors.push(...belowFloors.map( f => f.unit.name));
            }

            this.getFloorViewConfig('floors.outdoor', false) &&
                floors.push(...this.outdoorFloorsUnitNames);

            return floors;
        },
        floorViewCoversBuildingIds(){
            return this.getFloorViewConfig('show_outdoor', false) ? this.nonActiveBuildingGroupBuildingIDs : [];
        },
        floorViewShowOutdoor(){
            return this.getFloorViewConfig('show_outdoor', false)
        },

        activeBuilding(){
            return this.buildings.find( b => b.meta.building_index === this.activeBuildingId)
        },
        activeBuildingOrientation(){
            return this.activeBuilding?.meta.camera_rotation || this.getExploreConfig('orientation',0);
        },
        activeBuildingGroupId(){
            return this.activeBuilding?.meta.building_group_id;
        },
        activeBuildingFloors(){
            return this.floors.filter( f => f.meta.building_index === this.activeBuildingId)
        },
        activeBuildingFloorsIDs(){
            return this.activeBuildingFloors.map( f => f.unit.name)
        },
        activeBuildingGroupBuildings(){
            return this.activeBuilding ? this.buildings.filter( b => b.meta.building_group_id === this.activeBuilding.meta.building_group_id) : this.emptyArray
        },
        activeBuildingGroupBuildingIDs(){
            return this.activeBuildingGroupBuildings.map( b => b.meta.building_index)
        },
        activeBuildingGroupFloors(){
            const bgId = this.activeBuildingGroupId;
            return this.floors.filter( f => f.floor.building_group_id === bgId);
        },
        activeBuildingGroupLevelFloors(){
            const activeLevel = this.activeLevel;
            return this.activeBuildingGroupFloors.filter( f => f.meta.level === activeLevel);
        },
        activeBuildingGroupLevelFloorsUnitNames(){
            return this.activeBuildingGroupLevelFloors.map( f => f.unit.name );
        },
        activeBuildingGroupFloorsUnitNames(){
            return this.activeBuildingGroupFloors.map( f => f.unit.name );
        },
        activeBuildingGroupSeparateFloors(){
            const min = this.activeBuildingGroupMinEntranceLevels;
            const activeLevelSeparateDistance = this.getFloorViewConfig('rise_above_floors',0);
            return this.activeBuildingGroupFloors.reduce( (acc, f) => {
                if(f.floor.level > this.activeLevel && this.activeView === 'LevelView'){
                    acc[`${f.unit.name}`] = (f.floor.level - min) * this.separateDistance + activeLevelSeparateDistance;
                }
                else
                {
                    acc[`${f.unit.name}`] = (f.floor.level - min) * this.separateDistance;
                }
                return acc;
            }, {} );
        },
        activeBuildingGroupLevels(){
            return _.uniq(
                this.activeBuildingGroupFloors.map( f => f.meta.level)
            );
        },
        activeBuildingGroupMinLevel(){
            return Math.min(...this.activeBuildingGroupLevels);
        },
        activeBuildingGroupMaxLevel(){
            return Math.max(...this.activeBuildingGroupLevels);
        },
        activeBuildingGroupAvgLevel(){
            return Math.round(this.activeBuildingGroupMinLevel + (this.activeBuildingGroupMaxLevel - this.activeBuildingGroupMinLevel) / 2 );
        },
        activeBuildingGroupAvgLevelFloors(){
            const avgLevel = this.activeBuildingGroupAvgLevel;
            return this.activeBuildingGroupFloors.filter( f => f.meta.level === avgLevel);
        },
        activeBuildingGroupAvgLevelFloorsUnitNames(){
            return this.activeBuildingGroupAvgLevelFloors.map( f => f.unit.name );
        },
        activeBuildingGroupEntranceLevels(){
            const ids = this.activeBuildingGroupBuildingIDs;
            return _.uniq(
                this.entrances.reduce( (acc, p) => {
                    ids.includes(p.floor.building_index) && acc.push(p.floor.level);
                    return acc;
                }, [])
            );
        },
        activeBuildingGroupGroundLevels(){
            const ids = this.activeBuildingGroupBuildingIDs;
            return _.uniq(
                this.groundFloors.reduce( (acc, f) => {
                    ids.includes(f.meta.building_index) && acc.push(f.meta.level);
                    return acc;
                }, [])
            );
        },
        activeBuildingGroupMinEntranceLevels(){
            return Math.min(...this.activeBuildingGroupEntranceLevels, ...this.activeBuildingGroupGroundLevels );
        },
        activeBuildingGroupMaxEntranceLevels(){
            return Math.max(...this.activeBuildingGroupEntranceLevels);
        },
        activeBuildingGroupHasUnderground(){
            return this.activeBuildingGroupLevels.some( l => l < this.activeBuildingGroupMinEntranceLevels);
        },
        activeBuildingGroupTransparentFloors(){
            return this.activeBuildingGroupHasUnderground ? this.outdoorFloors : [];
        },
        activeBuildingGroupTransparentFloorsUnitNames(){
            return this.activeBuildingGroupTransparentFloors.map( f => f.unit.name);
        },
        nonActiveBuildingGroupBuildings(){
            return this.activeBuilding ? this.buildings.filter( b => b.meta.building_group_id !== this.activeBuilding.meta.building_group_id) : this.emptyArray
        },
        nonActiveBuildingGroupBuildingIDs(){
            return this.nonActiveBuildingGroupBuildings.map( b => b.meta.building_index)
        },

        activeBgFloorsObjects(){
            const bgFloorsIds = this.activeBuildingFloorsIDs;
            return this.objects.filter( o => bgFloorsIds.includes(o.floor.unit.name) );
        },
        activeBgLevelObjects(){
            return this.activeBgFloorsObjects.filter( o => o.floor.level === this.activeLevel );
        },
        activeBgFloorsLabels(){
            return this.activeBgFloorsObjects.filter( o => o.type === 'label' );
        },
        activeBgFloorsAnchors(){
            return this.activeBgFloorsObjects.filter( o => o.meta.anchor );
        },
        activeBgFloorsIcons(){
            const icons = [];

            this.getBgViewConfig('icons.anchors', false) &&
                icons.push(...this.activeBgFloorsAnchors.map( obj => {
                    switch (obj.type){
                        case 'portal':
                        case 'label':
                            return `${obj.id}--${obj.unit.name}`;
                        case 'tenant':
                        case 'service':
                            // acc.push(obj.id);
                            return `tenant-icon-${obj.id}-${obj.unit.name}`;
                    }
                }));

            this.getBgViewConfig('icons.labels', false) &&
                icons.push(...this.activeBgFloorsLabels.map( obj => `${obj.id}--${obj.unit.name}` ));

            return icons
        },
        activeLevelFloors(){
            return this.floors.filter( f => f.meta.level === this.activeLevel && this.activeBuildingGroupBuildingIDs.includes(f.meta.building_index))
        },
        levelViewIcons(){
            return this.$route.query.mLabels !== '0';
            // load active bg level icons
            const afAnchors = this.getFloorViewConfig('icons.active_floor_anchors', true);
            const afLabels = this.getFloorViewConfig('icons.active_floor_labels', true);
            const afSelected = this.getFloorViewConfig('icons.active_floor_selected', true);
            const afOther = this.getFloorViewConfig('icons.active_floor_other', true);

            const icons = this.activeBgLevelObjects.reduce( (acc, obj) => {
                switch (obj.type){
                    case 'portal':
                    case 'label':
                        afLabels &&
                            acc.push(`${obj.id}--${obj.unit.name}`);
                        break;
                    case 'tenant':
                    case 'service':
                        (   afOther ||
                            afAnchors && obj.meta.anchor ||
                            afSelected && this.selectedObjectsIds.includes(obj.id)
                        ) &&
                            acc.push(`${obj.id}--${obj.unit.name}`);
                        break;
                }
                return acc;
            }, []);

            this.getFloorViewConfig('icons.other_floors_anchors', false) &&
                icons.push(...this.activeBgFloorsAnchors.map( obj => {
                    return `${obj.id}--${obj.unit.name}`;
                }));

            this.getFloorViewConfig('icons.other_floors_labels', false) &&
                icons.push(...this.activeBgFloorsLabels.map( obj => `${obj.id}--${obj.unit.name}` ));

            return icons
        },

        indoorFloors(){
            return this.floors.filter( f => !f.meta.is_outdoor );
        },
        indoorFloorsUnitNames(){
            return this.indoorFloors.map( f => f.unit.name )
        },

        outdoorViewIcons(){
            // load active bg level icons
            return true;
            const afAnchors = this.getOutdoorViewConfig('icons.active_floor_anchors', true);
            const afLabels = this.getOutdoorViewConfig('icons.active_floor_labels', true);
            const afSelected = this.getOutdoorViewConfig('icons.active_floor_selected', true);
            const afOther = this.getOutdoorViewConfig('icons.active_floor_other', true);

            return  this.outdoorFloorObjects.reduce( (acc, obj) => {
                switch (obj.type){
                    case 'portal':
                    case 'label':
                        afLabels &&
                            acc.push(`${obj.id}--${obj.unit.name}`);
                        break;
                    case 'tenant':
                    case 'service':
                        (   afOther ||
                            afAnchors && obj.meta.anchor ||
                            afSelected && this.selectedObjectsIds.includes(obj.id)
                        ) &&
                            acc.push(`${obj.id}--${obj.unit.name}`);
                        break;
                }
                return acc;
            }, []);
        },

        activeLevelFloorIDs(){
            return this.activeLevelFloors.map( f => f.unit.name )
        },
        outdoorBuildingsIDs(){
            return this.buildings.filter( b => b.meta.is_outdoor)
        },

        outdoorFloors(){
            return this.floors.filter( f => f.meta.is_outdoor)
        },
        outdoorFloorsIds(){
            return this.outdoorFloors.map( f => f.id )
        },
        outdoorFloorsUnitNames(){
            return this.outdoorFloors.map( f => f.unit.name)
        },
        outdoorFloorsUnitNamesToZoomOn(){
            return this.getOutdoorViewConfig('zoom.zoom_on_outdoor_floors',false) ? this.outdoorFloorsUnitNames : [];
        },

        outdoorFloorObjects(){
            return this.objects.filter( o => this.outdoorFloorsIds.includes(o.floor.id) );
        },
        levelViewFloorIDs(){
            return this.activeLevel === 0 ? [...this.activeLevelFloorIDs, ...this.outdoorFloorsUnitNames] : this.activeLevelFloorIDs
        },

        levels(){
            return Array.from(this.floors.reduce((acc,f) => acc.add(f.meta.level), new Set()));
        },
        floors(){
            return this.mapViewer.mapData.objects.filter( o => o.type === 'floor');
        },
        allFloorsUnitNames(){
            return this.floors.map( f => f.unit.name)
        },
        objects(){
            return this.mapViewer.mapData.objects;
        },
        tenants(){
            return this.objects.filter( o => o.type === 'tenant');
        },
        services(){
            return this.objects.filter( o => o.type === 'service');
        },
        anchorTenants(){
            return this.tenants.filter( o => o.meta.anchor );
        },

        anchorServices(){
            return this.services.filter( o => o.meta.anchor );
        },
        anchorTenantUnitNames(){
            return this.anchorTenants.map( obj => obj.unit.name );
        },
        anchorServicesUnitNames(){
            return this.anchorServices.map( obj => obj.unit.name );
        },

        units(){
            return this.mapViewer.mapData.units;
        },
        unitNames(){
            return this.units.map( u => u.name);
        },
        modelUnits(){
            return this.units.filter( u => u.type === 'dae_unit')
        },
        modelUnitsNames(){
            return this.modelUnits.map( u => u.name);
        },

        portals(){
            return this.mapViewer.mapData.objects.filter( o => o.type === 'portal');
        },
        entrances(){
            return this.portals.filter( p => p.meta.is_entrance )
        },
        groundFloors(){
            return this.floors.filter( f => f.meta.is_ground )
        },

        buildings(){
            return this.mapViewer.mapData.objects.filter( o => o.type === 'building');
        },
        hoverableObjects(){
            return this.mapViewer.mapData.objects.filter( o => ['tenant', 'service', 'portal', 'floor', 'building'].includes(o.type));
        },
        indoorBuildings(){
            return this.buildings.filter( o => !o.meta.is_outdoor);
        },
        buildingIds(){
            return this.buildings.map( b => b.meta.building_index)
        },

        targetZoneServices(){
            const tzCatId = this.getMapConfigProp('categories.target_zone_service_category')
            if(!tzCatId){
                // TODO: Add/replace with sentry logging
                console.error('Target zone category is not defined. Set it in map settings `categories.target_zone_service_category`')
                return this.emptyArray
            }
            return this.md.objects.filter( o => o.type === 'service' && o.lists.categories && o.lists.categories.includes(tzCatId) && !this.activeLevelFloorIDs.includes(o.floor.unit_name) && o.files.icon )
        },
        targetZoneServicesUnitNames(){
            return this.targetZoneServices.map( o => o.unit.name )
        },

        activeLevelPortals(){
            return this.portals.filter( p => this.activeLevelFloorIDs.includes(p.floor.unit_name) )
        },
        activeBuildingPortals(){
            return this.portals.filter( p => this.activeBuildingId === p.floor.building_index && p.meta.portal_type === 'Elevators' )
        },
        activeLevelElevatorPortals(){
            return this.activeLevelPortals.filter( p => p.meta.portal_type === 'Elevators' )
        },

        objectsInFocusUnitNames(){
            return this.objectsInFocus.map( o => o.unit.name )
        },
        floorsInFocusUnitNames(){
            return this.floorsInFocus.map( f => f.unit.name )
        },
        selectedObjectsUnitNames(){
            return Array.from( this.selectedObjects.reduce( (acc,o) => acc.add(o.unit.name), new Set ) )
        },
        selectedObjectsIds(){
            return Array.from( this.selectedObjects.reduce( (acc,o) => acc.add(o.id), new Set ) )
        },
        floorsWallsHighlightColors(){
            const color =  this.getMapConfigProp(`floors.default.walls_color`);
            return color ? arrayValues2ObjectKeys(this.indoorFloorsUnitNames, color) : {};
        },
        unitsHighlightColors(){
            return _.merge(
                {},
                this.unitsColors,
                this.objectUnitColors,
                this.anchorTenantUnitsColors,
                this.anchorServicesUnitsColors,
                this.selectedObjectsUnitsColor
            )
        },
        unitsColors(){
            const color = this.getMapConfigProp(`map_views.default.units.color`);
            return color ? arrayValues2ObjectKeys(this.modelUnitsNames, color) : {};
        },
        selectedObjectsUnitsColor(){
            const selectedColor = this.getMapConfigProp(`map_views.default.units.selected_color`);
            return selectedColor ? objects2UnitColors(this.selectedObjects, 'selected_color', selectedColor) : {};
        },
        objectUnitColors(){
            const color = this.getMapConfigProp(`map_views.default.units.color`);
            return color ? objects2UnitColors(this.selectedObjects, 'color', color) : {};
        },

        anchorTenantUnitsColors(){
            const color = this.getMapConfigProp(`map_views.default.units.anchor_tenant_color`) ||
                this.getMapConfigProp(`map_views.default.units.anchor_color`);
            return objects2UnitColors(this.anchorTenants, 'anchor_color', color);
        },
        anchorServicesUnitsColors(){
            const color = this.getMapConfigProp(`map_views.default.units.anchor_service_color`) ||
                this.getMapConfigProp(`map_views.default.units.anchor_color`);
            return objects2UnitColors(this.anchorServices, 'anchor_color', color);
        },

        zoomOnUnitNames(){
            return this.selectedObjectsUnitNames//.length ? this.selectedObjectsUnitNames : this.targetZoneServicesUnitNames
        },
        hoveredUnitObjects(){
            if(!this.hoveredUnitName) {
                return this.emptyArray;
            }
            const sourceObjects = this.activeView === 'LevelView' ? this.activeBgLevelObjects : this.hoverableObjects;

            return sourceObjects.filter( o => o.unit.name === this.hoveredUnitName );
        },
        hoveredFloor(){
            return this.floors.find( f => f.unit.name === this.hoveredFloorUnitName )
        },
        hoveredFloorObjects(){
            return this.hoveredFloorUnitName ?
                this.hoverableObjects.reduce( (acc, obj) =>
                    obj.floor.unit_name === this.hoveredFloorUnitName && !this.selectedObjectsUnitNames.includes(obj.unit.name) && acc.add()
                ) : this.emptyArray
        },
        showOutdoor(){
            return this.activeLevel === null || this.getMapConfigProp('global_map.outdoor_levels', [0]).includes(this.activeLevel)
        },
        isDebug(){
            return document.location.href.includes('localhost');
        },
        targetObjectId(){
            return Number(this.$route.query.t || this.$route.query.mTo);
        },
    },

    // -------------------------------- Methods ---------------------------------------
    methods: {
        initEventListeners() {
            this.$bus.$on(MAP_SHOW_FLOOR, this.showFloor);
            this.$bus.$on(MAP_SHOW_OBJECT, this.showObject);
            this.$bus.$on(MAP_ICONS_LOADED, this.setIconsLoaded);
            //this.$bus.$on(SET_NORMALIZED_ZOOM, this.onZoom);
            this.$bus.$on(SET_GYROSCOPE_STATE, this.onGyroStateChange);

        },
        deactivateEventListeners() {
            this.$bus.$off(MAP_SHOW_FLOOR, this.showFloor);
            this.$bus.$off(MAP_SHOW_OBJECT, this.showObject);
            this.$bus.$off(MAP_ICONS_LOADED, this.setIconsLoaded);
            //this.$bus.$off(SET_NORMALIZED_ZOOM, this.onZoom);
            this.$bus.$off(SET_GYROSCOPE_STATE, this.onGyroStateChange);
        },
        onTransitionStart(){
            this.isViewInTransition = true;
        },
        onTransitionEnd(){
            this.isViewReady = true;
            this.isViewInTransition = false;
            this.resetFirstViewSettings();
        },
        resetFirstViewSettings(){
            if(this.mapViewer.$data.isFirstView)
            {
                this.mapViewer.$data.isFirstView = false;
            }
        },
        onGyroStateChange(isGyroEnabled){
            this.canOrient = isGyroEnabled
        },

        onFloorMouseOver(floorUnitName){
            if(floorUnitName && this.hoveredFloorUnitName !== floorUnitName)
                this.hoveredFloorUnitName = floorUnitName
            else
                this.hoveredFloorUnitName = null
        },
        onUnitMouseOver(unitName){
            if(unitName && this.hoveredUnitName !== unitName  &&!this.selectedObjectsUnitNames.includes(unitName))
            {
                clearTimeout(this.hoverDelayTimeout);
                this.hoverDelayTimeout = setTimeout( () => this.hoveredUnitName = unitName, 300 );
            }
            else
                this.hoveredUnitName = null
        },
        onRemovePath(){
            this.mapViewer.viewApi.remove_path()
            this.mapViewer.viewApi.restore_base_color()

            this.$store.commit('setActiveStepIndex', null);
            this.$store.commit('setActiveStep', null);
            this.$store.commit('setActivePathToState', null);

            this.$emit('changeView','OutdoorView')
        },
        onZoom(data){
            if(
                data &&
                data.mode !== 'zoom' &&
                !this.mapViewer.activeView.includes('Guide') &&
                this.mapViewer.viewApi.camera_controller.normalized_zoom > 0.4
            )
                this.goToOutdoorView()
        },
        onPortalClick(portal){
            this.resetSelected()
            this.activeBuildingId = portal.floor.building_index
            this.activeFloorUnitName = portal.floor.unit_name
            this.objectsInFocus = [portal]
            this.$emit('changeView','FloorSelection')
        },

        onFloorClick(floorUnit){
            const f = this.floors.find(f => f.unit.name === floorUnit);

            switch (this.activeView){
                case 'OutdoorView':
                    return;
                case 'FloorSelection':
                    if(!floorUnit || f?.meta.is_outdoor){
                        this.goToOutdoorView();
                        return;
                    }
                    break;
                case "LevelView":
                    if(!floorUnit || f?.meta.is_outdoor){
                        this.goToFloorSelectionView(this.activeBuildingId, this.activeLevel);
                        return;
                    }
                    if(f.floor.level === this.activeLevel && f.floor.building_index === this.activeBuildingId)
                    {
                        return;
                    }
            }

            this.activeUnitName = null;
            this.selectedObjects = this.emptyArray;
            this.hoveredUnitName = null;
            this.activeLevel = f.meta.level;
            this.activeBuildingId = f.meta.building_index;
            this.activeFloorUnitName = floorUnit;
            this.$emit('changeView','LevelView')
        },
        onUnitClick(unitName) {
            this.selectUnitObjects(unitName);
        },

        goToOutdoorView(){
            this.activeBuildingId = null
            this.activeLevel = null
            this.resetSelected()
            this.$emit('changeView','OutdoorView')
        },

        goToLevelView(buildingId, level = 0){
            this.resetSelected()
            this.activeBuildingId = buildingId
            this.activeLevel = level

            this.$emit('changeView','LevelView')
        },
        goToFloorSelectionView(buildingId, level = null){
            this.resetSelected();
            this.activeBuildingId = buildingId;
            this.activeLevel = level !== null ? level : this.activeBuildingGroupAvgLevel;
            this.$emit('changeView','FloorSelection');
        },
        goToLevelViewByObject(obj){
            this.resetSelected();
            this.activeBuildingId = obj.floor.building_index;
            this.activeLevel = obj.floor.level;
            this.objectsInFocus = [obj];

            this.$emit('changeView','LevelView')
        },
        showFloor({id}){
            const floor = this.findFloorById(id)
            floor && this.goToLevelView(floor.meta.building_index, floor.meta.level)
        },
        showObject({id, unit}){
            const o = this.findObject(id, unit);
            o && this.goToLevelViewByObject(o)
        },
        findFloorByBuildingAndLevel(buildingId, level){},
        findFloorById(id){
            return this.floors.find( f => f.id === id)
        },
        findObject(id, unit = null){
            return this.mapViewer.mapData.objects.find( f => (f.id === id || f.meta.otg_store_id === id) && !unit || f.unit.name === unit )
        },
        selectObject(obj){
            if(!obj) return;
            this.selectedObjects = [obj];
        },
        selectUnitObjects(unitName){
            if(!unitName) return;

            if(this.activeLevel !== null){
                const selectedObjects = this.activeBgLevelObjects.filter(o => o.unit.name === unitName);
                //this.hoverableObjects.filter(o => o.unit.name === unitName);

                if(selectedObjects.length) {
                    this.selectedObjects = selectedObjects;
                }
            }
        },
        selectedObjectsByUnitName(unitName){
            return this.selectedObjects.filter( o => o.unit.name === unitName)
        },

        requestGuideTo(obj){
            if (parseInt(this.$route.query.to, 10) === obj.id) return;
            const params = {
                name: `${this.$route.name.split('.')[0]}.guide`,
                params: {
                    type: 'start'
                },
                query: {
                    to: obj.id
                }
            };
            //this.activeLevel = null;
            //this.activeBuildingId = null;
            //this.resetSelected();
            this.$router.push(params)
        },
        requestGuideFrom(obj){
            if (parseInt(this.$route.query.from, 10) === obj.id) return;
            const params = {
                name: `${this.$route.name.split('.')[0]}.guide`,
                params: {
                    type: 'start'
                },
                query: {
                    from: obj.id
                }
            };
            this.$router.push(params)
        },
        resetSelected(){
            this.objectsInFocus = [];
            this.selectedObjects = [];
            this.hoveredUnitName = null;
            this.hoveredFloorName = null;
        },
        getExploreConfig(prop, def){
            return _.get( this.mapViewer.mapData.config, `explore_camera.${prop}`, def);
        },
        getBgViewConfig(prop, def){
            return this.getMapConfigProp(`map_views.building_group.${prop}`, def)  || this.getMapConfigProp(`map_views.default.${prop}`, def);
        },
        getOutdoorViewConfig(prop, def){
            return this.getMapConfigProp(`map_views.outdoor.${prop}`) || this.getMapConfigProp(`map_views.default.${prop}`, def);
        },
        getFloorViewConfig(prop, def){
            const floorViewValue = this.getMapConfigProp(`map_views.floor.${prop}`);
            const defaultViewValue = this.getMapConfigProp(`map_views.default.${prop}`, def);
            return floorViewValue !== null ? floorViewValue : defaultViewValue;
        },
        initFromQuery(){
            if(this.$route.query['map.transition']){
                this.transitionDuration = Number(this.$route.query['map.transition'])
            }
            if(this.$route.query.mvb)
            {
                this.activeBuildingId = this.$route.query.mvb;
            }
        },

        setIconsLoaded(){
            this.areIconsLoaded = true;
            this.renderTrigger++;
        }

    },

    // -------------------------------- Watch ---------------------------------------
    watch:{
        hoveredUnitObjects(){
            // this.hoveredUnitObjects.forEach( o => this.$bus.$emit(MAP_OBJECT_HOVERED , o))
        },
        objectsInFocus(){
            this.objectsInFocus.forEach( o => this.$bus.$emit(MAP_OBJECT_SELECTED, o))
        },
        activeBuildingId(){
            console.log('building change');
        },
        activeView(view){
            if(view === 'ExplorationViews') {
                this.$emit('changeView',this.prevActiveView);
            }
            if(view === 'OutdoorView'){
                this.selectedObjects = [];
            }
            if(view === 'LevelView'){
                if (this.targetObjectId) {
                    const obj = this.findObject(Number(this.targetObjectId));
                    if (obj && this.activeView !== "GuideView") {
                        this.goToLevelViewByObject(obj);
                        this.selectObject(obj);
                    }
                }
            }
            if(this.isActive){
                this.prevActiveView = view;
            }
            this.initFromQuery();
        },
        visibleFloorsUnitNames(){
            this.prevViewFloorsUnitNames = this.allFloorsUnitNames;
        },
        isActive(){
            if(this.isActive){
                this.initEventListeners();
            }
            else {
                this.deactivateEventListeners();
                //this.activeBuilding = null;
                //this.activeLevel = null;
            }
        },
        targetObjectId:{
            handler(objectId) {
                if (objectId) {
                    const obj = this.findObject(Number(objectId));
                    if (obj && this.activeView !== "GuideView") {
                        this.goToLevelViewByObject(obj);
                        this.selectObject(obj);
                    }
                }
            },
            immediate: true,
        },
    },

    // -------------------------------- Hooks ---------------------------------------
    mounted() {
        this.initEventListeners();
        this.initFromQuery();
    },
    beforeDestroy() {
        this.deactivateEventListeners()
    },
}
</script>
<style lang="scss">
  @import "o-map-exploration-views";
</style>

